import { getSupabaseClient } from "../providers/supabase/supabase";

/**
 * Client for an agency's Studio billing (real-estate films, packs and monthly
 * plans; bot docs/studio-billing.md). The wallet lives in the WhatsApp bot, so
 * it is reached through its own Vercel proxy (src/server/studioBillingProxy.ts),
 * the same shape as the contact-language and test-echo clients: the browser
 * only sends its Supabase access token; the proxy checks the administrator,
 * injects the bot key and stamps the verified operator e-mail on grants.
 *
 *   GET  /api/studio-billing/billing/wallet?agency_id=<uuid>
 *   GET  /api/studio-billing/billing/orders?agency_id=<uuid>
 *   POST /api/studio-billing/ops/agencies/<uuid>/grants
 */
const STUDIO_BILLING_PROXY_PATH = "/api/studio-billing";

export type StudioBillingMode = "exempt" | "standard";
export type StudioPlanStatus =
  | "none"
  | "active"
  | "past_due"
  | "canceling"
  | "ended";

export type StudioOffer = {
  offer_id: string;
  kind: "one_time" | "plan";
  films: number;
  amount_minor: number;
  list_amount_minor: number | null;
  per_film_minor: number;
  currency: string;
  label: string;
  plan: {
    interval: string;
    rollover_cap_periods: number;
    lapse_days_after_end: number;
  } | null;
};

export type StudioPlan = {
  status: StudioPlanStatus;
  offer_id: string | null;
  films_per_period: number | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  lapse_at: string | null;
};

/** The four wallet counters (films; debt is films owed). */
export type StudioWalletCounters = {
  free_credits: number;
  paid_credits: number;
  plan_credits: number;
  debt: number;
};

export type StudioWallet = StudioWalletCounters & {
  enabled: true;
  billing_mode: StudioBillingMode;
  agency_status: "active" | "paused";
  films_available: number;
  price: { amount_minor: number; currency: string; credits: number };
  price_book_id: string | null;
  currency: string;
  offers: StudioOffer[];
  intro: { label: string; ends_at: string } | null;
  custom_above_films_per_month: number | null;
  plan: StudioPlan;
  can_manage_billing: boolean;
};

/** `{enabled:false}`: billing is off in this environment (the bot is unmetered). */
export type StudioWalletState = { enabled: false } | StudioWallet;

export type StudioOrder = {
  order_id: string;
  status: string;
  amount_minor: number | null;
  currency: string | null;
  films: number | null;
  offer_id: string | null;
  kind: string;
  created_at: string | null;
};

export type StudioGrantKind = "comp" | "goodwill" | "invoiced";
export type StudioGrantCounter = "free" | "paid";

export type StudioGrantRequest = {
  grant_id: string;
  films: number;
  kind: StudioGrantKind;
  counter: StudioGrantCounter;
  reason: string;
};

export type StudioGrantResult = {
  /** false = a replay: this grant id was already applied, nothing was added. */
  granted: boolean;
  wallet: StudioWalletCounters;
};

export type StudioBillingErrorCode =
  | "unauthorized"
  | "forbidden"
  | "billing_off"
  | "not_found"
  | "invalid_request"
  | "unconfigured"
  | "auth_unavailable"
  | "unavailable"
  | "malformed";

export class StudioBillingRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: StudioBillingErrorCode,
    /** The proxy's or the bot's own error code, as sent. */
    readonly upstreamCode: string,
    /** Per-field reasons from a 422. */
    readonly fields: Record<string, string> = {},
  ) {
    super(upstreamCode);
    this.name = "StudioBillingRequestError";
  }
}

/* ------------------------------------------------------------------------- */
/* Requests                                                                  */
/* ------------------------------------------------------------------------- */

export async function fetchAgencyWallet(
  agencyId: string,
): Promise<StudioWalletState> {
  return parseWalletState(
    await studioBillingRequest(
      `billing/wallet?agency_id=${encodeURIComponent(agencyKey(agencyId))}`,
      "read",
    ),
  );
}

export async function fetchAgencyOrders(
  agencyId: string,
): Promise<StudioOrder[]> {
  return parseOrders(
    await studioBillingRequest(
      `billing/orders?agency_id=${encodeURIComponent(agencyKey(agencyId))}`,
      "read",
    ),
  );
}

export async function grantAgencyFilms(
  agencyId: string,
  grant: StudioGrantRequest,
): Promise<StudioGrantResult> {
  return parseGrantResult(
    await studioBillingRequest(
      `ops/agencies/${encodeURIComponent(agencyKey(agencyId))}/grants`,
      "grant",
      { method: "POST", body: JSON.stringify(grant) },
    ),
  );
}

async function authHeaders(): Promise<Record<string, string>> {
  try {
    const { data } = await getSupabaseClient().auth.getSession();
    const token = data.session?.access_token;
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

async function studioBillingRequest(
  path: string,
  context: "read" | "grant",
  init: RequestInit = {},
): Promise<Record<string, unknown>> {
  let response: Response;
  try {
    response = await fetch(`${STUDIO_BILLING_PROXY_PATH}/${path}`, {
      ...init,
      headers: {
        Accept: "application/json",
        ...(init.body ? { "Content-Type": "application/json" } : {}),
        ...(await authHeaders()),
      },
    });
  } catch {
    throw new StudioBillingRequestError(0, "unavailable", "network_error");
  }
  const text = await response.text();
  let payload: unknown = null;
  try {
    payload = text ? JSON.parse(text) : null;
  } catch {
    payload = null;
  }
  const record =
    payload && typeof payload === "object" && !Array.isArray(payload)
      ? (payload as Record<string, unknown>)
      : null;
  if (!response.ok) {
    throw studioBillingErrorFromResponse(
      response.status,
      record ?? {},
      context,
    );
  }
  if (!record) {
    // A 200 that is not JSON: local `vite dev` has no /api functions and
    // answers with the SPA's index.html.
    throw new StudioBillingRequestError(
      response.status,
      "malformed",
      "studio_billing_malformed_response",
    );
  }
  return record;
}

/** Map a proxy or bot refusal onto a typed error. Pure, so it is unit-tested. */
export function studioBillingErrorFromResponse(
  status: number,
  payload: Record<string, unknown>,
  context: "read" | "grant" = "read",
): StudioBillingRequestError {
  const upstreamCode =
    stringOr(payload.error, "") ||
    stringOr(payload.code, "") ||
    `http_${status}`;
  const fields =
    payload.fields && typeof payload.fields === "object"
      ? Object.fromEntries(
          Object.entries(payload.fields as Record<string, unknown>).map(
            ([field, reason]) => [field, String(reason)],
          ),
        )
      : {};
  const code: StudioBillingErrorCode =
    status === 401
      ? "unauthorized"
      : status === 403
        ? "forbidden"
        : status === 404
          ? context === "grant" && upstreamCode !== "not_found"
            ? // The bot's own 404 (`not found`): billing is off here.
              "billing_off"
            : "not_found"
          : status === 422 || status === 400
            ? "invalid_request"
            : upstreamCode === "studio_billing_unconfigured"
              ? "unconfigured"
              : upstreamCode === "auth_verification_unavailable"
                ? "auth_unavailable"
                : "unavailable";
  return new StudioBillingRequestError(status, code, upstreamCode, fields);
}

/** What to tell the operator, in one sentence. */
export function studioBillingErrorMessage(error: unknown): string {
  if (!(error instanceof StudioBillingRequestError)) {
    return "Something went wrong reading billing. Try again.";
  }
  switch (error.code) {
    case "unauthorized":
      return "Your CRM session has expired. Sign in again.";
    case "forbidden":
      return error.upstreamCode === "operator_email_required"
        ? "Your CRM sign-in has no e-mail address, and every grant is audited under one."
        : "Only CRM administrators can see or change agency billing.";
    case "billing_off":
      return "Billing is off in this environment, so there is nothing to grant.";
    case "not_found":
      return "The billing proxy does not know this request.";
    case "invalid_request": {
      const reasons = Object.entries(error.fields)
        .map(([field, reason]) => `${field}: ${reason}`)
        .join("; ");
      return reasons
        ? `The bot refused the request (${reasons}).`
        : "The bot refused the request.";
    }
    case "unconfigured":
      return "This CRM deployment has no Studio binding (STUDIO_BOT_BASE_URL / STUDIO_PORTAL_API_KEY).";
    case "auth_unavailable":
      return "The CRM could not verify your session with Supabase right now. Retry in a moment.";
    case "malformed":
      return "The billing proxy did not answer with JSON (local vite dev has no /api functions).";
    default:
      return "The bot's billing API is not answering right now. Retry in a moment.";
  }
}

/* ------------------------------------------------------------------------- */
/* Parsing (the bot is the authority; a malformed field degrades, not crashes) */
/* ------------------------------------------------------------------------- */

export function parseWalletState(
  payload: Record<string, unknown>,
): StudioWalletState {
  if (payload.enabled === false) return { enabled: false };
  if (payload.enabled !== true) {
    throw new StudioBillingRequestError(
      200,
      "malformed",
      "studio_billing_malformed_wallet",
    );
  }
  const currency = stringOr(payload.currency, "EUR").toUpperCase();
  const price = record(payload.price);
  const plan = record(payload.plan);
  const intro = record(payload.intro);
  return {
    enabled: true,
    billing_mode: payload.billing_mode === "exempt" ? "exempt" : "standard",
    agency_status: payload.agency_status === "paused" ? "paused" : "active",
    ...parseCounters(payload),
    films_available: count(payload.films_available),
    price: {
      amount_minor: count(price.amount_minor),
      currency: stringOr(price.currency, currency).toUpperCase(),
      credits: count(price.credits) || 1,
    },
    price_book_id: nullableString(payload.price_book_id),
    currency,
    offers: Array.isArray(payload.offers)
      ? payload.offers
          .map((offer) => parseOffer(record(offer), currency))
          .filter((offer): offer is StudioOffer => offer !== null)
      : [],
    intro:
      nullableString(intro.label) && nullableString(intro.ends_at)
        ? { label: String(intro.label), ends_at: String(intro.ends_at) }
        : null,
    custom_above_films_per_month: nullableCount(
      payload.custom_above_films_per_month,
    ),
    plan: {
      status: PLAN_STATUSES.has(plan.status as StudioPlanStatus)
        ? (plan.status as StudioPlanStatus)
        : "none",
      offer_id: nullableString(plan.offer_id),
      films_per_period: nullableCount(plan.films_per_period),
      current_period_end: nullableString(plan.current_period_end),
      cancel_at_period_end: plan.cancel_at_period_end === true,
      lapse_at: nullableString(plan.lapse_at),
    },
    can_manage_billing: payload.can_manage_billing === true,
  };
}

export function parseOrders(payload: Record<string, unknown>): StudioOrder[] {
  if (!Array.isArray(payload.orders)) {
    throw new StudioBillingRequestError(
      200,
      "malformed",
      "studio_billing_malformed_orders",
    );
  }
  return payload.orders.map((entry) => {
    const order = record(entry);
    return {
      order_id: stringOr(order.order_id, ""),
      status: stringOr(order.status, "unknown"),
      amount_minor: nullableCount(order.amount_minor),
      currency: nullableString(order.currency)?.toUpperCase() ?? null,
      films: nullableCount(order.films ?? order.credits),
      offer_id: nullableString(order.offer_id),
      kind: stringOr(order.kind, "one_time"),
      created_at: nullableString(order.created_at),
    };
  });
}

export function parseGrantResult(
  payload: Record<string, unknown>,
): StudioGrantResult {
  if (typeof payload.granted !== "boolean") {
    throw new StudioBillingRequestError(
      200,
      "malformed",
      "studio_billing_malformed_grant",
    );
  }
  return {
    granted: payload.granted,
    wallet: parseCounters(record(payload.wallet)),
  };
}

const PLAN_STATUSES = new Set<StudioPlanStatus>([
  "none",
  "active",
  "past_due",
  "canceling",
  "ended",
]);

function parseOffer(
  offer: Record<string, unknown>,
  currency: string,
): StudioOffer | null {
  const offerId = nullableString(offer.offer_id);
  if (!offerId) return null;
  const plan = offer.plan ? record(offer.plan) : null;
  return {
    offer_id: offerId,
    kind: offer.kind === "plan" ? "plan" : "one_time",
    films: count(offer.films),
    amount_minor: count(offer.amount_minor),
    list_amount_minor: nullableCount(offer.list_amount_minor),
    per_film_minor: count(offer.per_film_minor),
    currency: stringOr(offer.currency, currency).toUpperCase(),
    label: stringOr(offer.label, offerId),
    plan: plan
      ? {
          interval: stringOr(plan.interval, "month"),
          rollover_cap_periods: count(plan.rollover_cap_periods),
          lapse_days_after_end: count(plan.lapse_days_after_end),
        }
      : null,
  };
}

function parseCounters(source: Record<string, unknown>): StudioWalletCounters {
  return {
    free_credits: count(source.free_credits),
    paid_credits: count(source.paid_credits),
    plan_credits: count(source.plan_credits),
    debt: count(source.debt),
  };
}

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

/** Integers arrive as numbers, or as numeric strings from a DynamoDB Decimal. */
function nullableCount(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? Math.trunc(parsed) : null;
}

function count(value: unknown): number {
  return nullableCount(value) ?? 0;
}

function nullableString(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function stringOr(value: unknown, fallback: string): string {
  return nullableString(value) ?? fallback;
}

/** Supabase agency ids are lowercase uuids; the proxy accepts nothing else. */
function agencyKey(agencyId: string): string {
  return String(agencyId).trim().toLowerCase();
}

/* ------------------------------------------------------------------------- */
/* Grants                                                                    */
/* ------------------------------------------------------------------------- */

/** The bot's grant-id rule (contract C7). */
export const GRANT_ID_PATTERN = /^[a-z0-9][a-z0-9-]{5,79}$/;
export const MAX_GRANT_FILMS = 500;

/** Comps and goodwill are free films; an invoiced deal is bought films. */
export const GRANT_COUNTER: Record<StudioGrantKind, StudioGrantCounter> = {
  comp: "free",
  goodwill: "free",
  invoiced: "paid",
};

export const GRANT_KINDS: {
  id: StudioGrantKind;
  label: string;
  hint: string;
}[] = [
  {
    id: "comp",
    label: "Comp",
    hint: "A gift: a launch partner, a demo, a trial extension.",
  },
  {
    id: "goodwill",
    label: "Goodwill",
    hint: "Making up for a failed or disappointing film.",
  },
  {
    id: "invoiced",
    label: "Invoiced",
    hint: "A deal paid outside Stripe (an invoice or a bank transfer).",
  },
];

/** "free" or "bought": what the operator reads for each counter. */
export function counterLabel(counter: StudioGrantCounter): string {
  return counter === "free" ? "free" : "bought";
}

/** "Add 5 free films" / "Add 1 bought film". */
export function grantAction(films: number, kind: StudioGrantKind): string {
  const noun = films === 1 ? "film" : "films";
  return `Add ${films} ${counterLabel(GRANT_COUNTER[kind])} ${noun}`;
}

/** "Add 5 free films to Casa Sol": exactly what the confirm button does. */
export function grantSummary(
  films: number,
  kind: StudioGrantKind,
  agencyName: string,
): string {
  return `${grantAction(films, kind)} to ${agencyName}`;
}

/** React Query keys; the grant invalidates the agency's prefix. */
export const studioBillingKeys = {
  agency: (agencyId: string) => ["studio-billing", agencyId] as const,
  wallet: (agencyId: string) => ["studio-billing", agencyId, "wallet"] as const,
  orders: (agencyId: string) => ["studio-billing", agencyId, "orders"] as const,
};

/**
 * `crm-<kind>-<yyyy-mm-dd>-<agency slug or first 8 of id>-<random4>`, always
 * within {@link GRANT_ID_PATTERN}. The bot applies a grant id once, so the
 * dialog keeps one id per confirmed grant and a retry replays, never doubles.
 */
export function generateGrantId(
  kind: StudioGrantKind,
  agency: { id: string | number; slug?: string | null },
  now: Date = new Date(),
  suffix: () => string = randomSuffix,
): string {
  const date = now.toISOString().slice(0, 10);
  const tag = slugTag(agency.slug) || idTag(agency.id);
  return `crm-${kind}-${date}-${tag}-${suffix()}`;
}

function slugTag(slug: string | null | undefined): string {
  return (slug ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+/, "")
    .slice(0, 40)
    .replace(/-+$/, "");
}

function idTag(id: string | number): string {
  return (
    String(id)
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "")
      .slice(0, 8) || "agency"
  );
}

const SUFFIX_ALPHABET = "abcdefghijklmnopqrstuvwxyz0123456789";

function randomSuffix(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(4));
  return Array.from(bytes, (byte) => SUFFIX_ALPHABET[byte % 36]).join("");
}

/* ------------------------------------------------------------------------- */
/* Formatting                                                                */
/* ------------------------------------------------------------------------- */

/**
 * "€25.00" from 2500 minor units, in the currency's own decimals (as
 * contacts/profile/purchasesModel.ts formats purchases). Null when unknown.
 */
export function formatMinor(
  minor: number | null | undefined,
  currency: string | null | undefined,
): string | null {
  if (typeof minor !== "number" || !Number.isFinite(minor)) return null;
  const code = (currency || "EUR").toUpperCase();
  try {
    const formatter = new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: code,
    });
    const digits = formatter.resolvedOptions().maximumFractionDigits ?? 2;
    return formatter.format(minor / 10 ** digits);
  } catch {
    return `${(minor / 100).toFixed(2)} ${code}`;
  }
}

/** "19 Nov 2026" (UTC, so every operator reads the same day). */
export function formatBillingDate(
  iso: string | null | undefined,
): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  }).format(date);
}

/** One line for the plan: its state and the date that matters next. */
export function planSummary(plan: StudioPlan, now: Date = new Date()): string {
  const perMonth = plan.films_per_period
    ? `${plan.films_per_period} films a month`
    : null;
  const periodEnd = formatBillingDate(plan.current_period_end);
  const lapseDate = formatBillingDate(plan.lapse_at);
  const lapsed =
    plan.lapse_at !== null &&
    new Date(plan.lapse_at).getTime() <= now.getTime();
  const lapse =
    lapseDate && (lapsed ? `lapsed ${lapseDate}` : `lapse ${lapseDate}`);
  const parts: (string | null)[] = (() => {
    switch (plan.status) {
      case "active":
        return plan.cancel_at_period_end
          ? ["Canceling", perMonth, periodEnd && `ends ${periodEnd}`]
          : ["Active", perMonth, periodEnd && `renews ${periodEnd}`];
      case "canceling":
        return ["Canceling", perMonth, periodEnd && `ends ${periodEnd}`];
      case "past_due":
        return [
          "Payment past due",
          perMonth,
          "no new films until the invoice is paid",
        ];
      case "ended":
        return ["Ended", lapse && `plan films ${lapse}`];
      default:
        return ["No monthly plan"];
    }
  })();
  return parts.filter(Boolean).join(" · ");
}
